import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createMedia, fetchMediaItem, updateMedia } from '../../api/mediaApi';
import { placeholderSvgDataUrl } from '../../utils/placeholderImage';
import useIsMobile from '../../utils/useIsMobile';

const inputBase = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
  color: '#F3F4F6', fontSize: '14px', fontFamily: 'inherit', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.2s',
};

const AdminMediaEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new' || !id;

  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState({
    title: '', type: 'image', description: '', category: '',
    status: 'active', is_featured: false, keepExistingFile: true, keepExistingThumbnail: true,
  });
  const [existing, setExisting] = useState(null);
  const [file, setFile] = useState(null);
  const [thumb, setThumb] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let mounted = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const m = await fetchMediaItem(id);
        if (!mounted) return;
        setExisting(m);
        setForm({
          title: m.title || '', type: m.type || 'image', description: m.description || '',
          category: m.category || '', status: m.status || 'active',
          is_featured: Boolean(m.is_featured), keepExistingFile: true, keepExistingThumbnail: true,
        });
      } catch (e) { if (mounted) setErr(e?.message || 'Failed to load media'); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [id, isNew]);

  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  const thumbPreview = useMemo(() => (thumb ? URL.createObjectURL(thumb) : ''), [thumb]);
  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); if (thumbPreview) URL.revokeObjectURL(thumbPreview); }, [filePreview, thumbPreview]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('title', form.title); fd.append('type', form.type);
      fd.append('description', form.description); fd.append('category', form.category);
      fd.append('status', form.status); fd.append('is_featured', form.is_featured ? '1' : '0');
      fd.append('keepExistingFile', form.keepExistingFile ? 'true' : 'false');
      fd.append('keepExistingThumbnail', form.keepExistingThumbnail ? 'true' : 'false');
      if (file) fd.append('file', file);
      if (thumb) fd.append('thumbnail', thumb);
      const saved = isNew ? await createMedia(fd) : await updateMedia(id, fd);
      navigate(`/admin/media/${saved.id}`, { replace: true });
    } catch (e2) { setErr(e2?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const previewImageSrc = form.type === 'image'
    ? filePreview || existing?.file_url || placeholderSvgDataUrl({ width: 900, height: 520, text: 'Image' })
    : thumbPreview || existing?.thumbnail || placeholderSvgDataUrl({ width: 900, height: 520, text: 'Video' });

  const onFocus = (e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.5)'; };
  const onBlur = (e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#F9FAFB' }}>{isNew ? 'Add Media' : 'Edit Media'}</h1>
          <p style={{ margin: '6px 0 0', color: '#9CA3AF', fontSize: '14px' }}>{isNew ? 'Upload new media' : 'Update media details'}</p>
        </div>
        <button onClick={() => navigate('/admin/media')} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: '#FFD700', cursor: 'pointer', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back to Media
        </button>
      </div>

      {err && (
        <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', fontSize: '14px', marginBottom: '20px' }}>{err}</div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 0.6fr', gap: '20px' }}>
          <form onSubmit={submit} style={{ background: '#0F1B2D', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.05)', padding: isMobile ? '16px' : '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Title <span style={{ color: '#EF4444' }}>*</span></label>
                <input required value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} placeholder="e.g. Retail Installation"
                  style={inputBase} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Type</label>
                  <select value={form.type} onChange={(e) => setForm((s) => ({ ...s, type: e.target.value }))}
                    style={{ ...inputBase, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '32px' }}
                    onFocus={onFocus} onBlur={onBlur}>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Status</label>
                  <select value={form.status} onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
                    style={{ ...inputBase, cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: '32px' }}
                    onFocus={onFocus} onBlur={onBlur}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Category (optional)</label>
                <input value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} placeholder="e.g. installations / events"
                  style={inputBase} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>File Upload ({form.type === 'image' ? 'Image' : 'Video'})</label>
                <input type="file" accept={form.type === 'image' ? 'image/*' : 'video/*'} onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ color: '#9CA3AF', fontSize: '13px' }} />
                {!isNew && (
                  <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#D1D5DB', fontSize: '13px', marginTop: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.keepExistingFile} onChange={(e) => setForm((s) => ({ ...s, keepExistingFile: e.target.checked }))} />
                    Keep existing file
                  </label>
                )}
              </div>

              {form.type === 'video' && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Thumbnail (optional)</label>
                  <input type="file" accept="image/*" onChange={(e) => setThumb(e.target.files?.[0] || null)} style={{ color: '#9CA3AF', fontSize: '13px' }} />
                  {!isNew && (
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#D1D5DB', fontSize: '13px', marginTop: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.keepExistingThumbnail} onChange={(e) => setForm((s) => ({ ...s, keepExistingThumbnail: e.target.checked }))} />
                      Keep existing thumbnail
                    </label>
                  )}
                </div>
              )}

              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#D1D5DB', fontSize: '14px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm((s) => ({ ...s, is_featured: e.target.checked }))} />
                Featured on homepage
              </label>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#9CA3AF', marginBottom: '6px' }}>Description</label>
                <textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} placeholder="Write description..."
                  style={{ ...inputBase, minHeight: '120px', resize: 'vertical' }} onFocus={onFocus} onBlur={onBlur} />
              </div>

              <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
                <button type="submit" disabled={saving}
                  style={{ padding: '10px 28px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#0B1020', fontWeight: 600, fontSize: '14px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving...' : isNew ? 'Save Media' : 'Update Media'}
                </button>
                {!isNew && (
                  <button type="button" onClick={() => navigate(`/media/${id}`)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: '#FFD700', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>
                    View on site
                  </button>
                )}
              </div>
            </div>
          </form>

          <div style={{ background: '#0F1B2D', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.05)', padding: '20px', alignSelf: 'start' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px', color: '#FFD700', fontSize: '14px' }}>Preview</div>
            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
              <img src={previewImageSrc} alt="Preview" style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }} />
            </div>
            {!isNew && existing?.type === 'video' && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280', wordBreak: 'break-all' }}>
                Video: <span style={{ color: '#9CA3AF' }}>{existing.file_url}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AdminMediaEdit;
